import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, MenuItem, Checkbox, FormControlLabel, Typography, IconButton, Chip,
  Button, Alert, CircularProgress, Collapse, useTheme } from '@mui/material';
import { ArrowBack, ExpandMore, ExpandLess, CloudUploadOutlined, Close } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import type { SupportFormData } from '../../types';
import { SUPPORT_CATEGORIES, SUPPORT_IMAGE_TYPES, SUPPORT_IMAGE_LIMIT, SUPPORT_IMAGE_MAX_BYTES, validParticipantCount } from '../../config/support';

interface SupportFormProps {
  formData: SupportFormData;
  setField: <K extends keyof SupportFormData>(key: K, value: SupportFormData[K]) => void;
  onBack: () => void;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  researcherEmail: string;
  browserInfo: string;
  addScreenshots: (files: File[]) => void;
  removeScreenshot: (id: string) => void;
  studies: Array<{ id: string; name: string }>;
}

const ENROLLMENT_STEPS = ['Receiving the invitation', 'Opening the invitation link', 'Connecting Fitbit / Google', 'Entering participant details', 'Other / not sure'];

export default function SupportForm({ formData, setField, onBack, onSubmit, isSubmitting, error,
  researcherEmail, browserInfo, addScreenshots, removeScreenshot, studies }: SupportFormProps) {
  const theme = useTheme();
  const [showMore, setShowMore] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const panelRef = useRef<HTMLFormElement>(null);
  const category = SUPPORT_CATEGORIES[formData.category!];
  const has = (field: typeof category.fields[number]) => category.fields.includes(field);
  const invalidCount = has('participants') && !validParticipantCount(formData.participantsAffected);
  const remaining = SUPPORT_IMAGE_LIMIT - formData.screenshots.length;
  // Menus use a portal. Keep them above the floating support panel.
  const selectProps = { MenuProps: { sx: { zIndex: theme.zIndex.modal + 2 }, PaperProps: { sx: { maxHeight: 240 } } } };
  const studyOptions = formData.studyId && !studies.some(study => study.id === formData.studyId)
    ? [{ id: formData.studyId, name: formData.studyName || 'Current study' }, ...studies] : studies;

  useEffect(() => {
    const paste = (event: ClipboardEvent) => {
      if (isSubmitting || !panelRef.current?.contains(event.target as Node)) return;
      const images = Array.from(event.clipboardData?.items || [])
        .filter(item => item.type.startsWith('image/')).map(item => item.getAsFile()).filter((file): file is File => !!file);
      if (images.length) { event.preventDefault(); setUploadError(''); addScreenshots(images); }
    };
    document.addEventListener('paste', paste);
    return () => document.removeEventListener('paste', paste);
  }, [addScreenshots, isSubmitting]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: Object.fromEntries(SUPPORT_IMAGE_TYPES.map(type => [type, []])),
    maxFiles: Math.max(1, remaining), maxSize: SUPPORT_IMAGE_MAX_BYTES,
    disabled: isSubmitting || remaining === 0,
    onDrop: (accepted, rejected) => {
      if (rejected.length) { setUploadError('Choose up to 3 JPG, PNG, GIF or WebP images, no larger than 5 MB each.'); return; }
      setUploadError(''); addScreenshots(accepted);
    },
  });

  return <Box component="form" ref={panelRef} onSubmit={event => { event.preventDefault(); void onSubmit(); }}
    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <IconButton size="small" onClick={onBack} disabled={isSubmitting} aria-label="Back to categories"><ArrowBack fontSize="small" /></IconButton>
      <Chip label={category.label} size="small" color="primary" variant="outlined" />
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>Reply to: {researcherEmail}</Typography>
    <TextField id="support-description" label="What happened?" placeholder={category.prompt} multiline minRows={3}
      fullWidth required disabled={isSubmitting} value={formData.description}
      onChange={event => setField('description', event.target.value)} inputProps={{ minLength: 10, maxLength: 5000 }}
      helperText={formData.description.trim().length < 10 ? 'Add a little detail (at least 10 characters).' : formData.description.length.toLocaleString() + ' / 5,000 characters'} />

    {has('study') && <TextField id="support-study" select label="Study (optional)" size="small" fullWidth
      disabled={isSubmitting} value={formData.studyId} SelectProps={selectProps} onChange={event => {
        setField('studyId', event.target.value);
        setField('studyName', studyOptions.find(study => study.id === event.target.value)?.name || '');
      }}>
      <MenuItem value="">No specific study</MenuItem>
      {studyOptions.map(study => <MenuItem key={study.id} value={study.id}>{study.name}</MenuItem>)}
    </TextField>}

    {has('participant') && <TextField id="support-participant" label="Participant ID (optional)" size="small"
      placeholder="Participant ID or tracking ID" disabled={isSubmitting} value={formData.participantIdentifier}
      onChange={event => setField('participantIdentifier', event.target.value)} inputProps={{ maxLength: 200 }} />}

    {has('enrollment') && <TextField id="support-enrollment" select label="Where did it stop? (optional)" size="small"
      disabled={isSubmitting} value={formData.enrollmentStep} SelectProps={selectProps}
      onChange={event => setField('enrollmentStep', event.target.value)}>
      <MenuItem value="">Not sure</MenuItem>
      {formData.enrollmentStep && !ENROLLMENT_STEPS.includes(formData.enrollmentStep) && <MenuItem value={formData.enrollmentStep}>{formData.enrollmentStep}</MenuItem>}
      {ENROLLMENT_STEPS.map(step => <MenuItem key={step} value={step}>{step}</MenuItem>)}
    </TextField>}

    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>Screenshots (optional)</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: formData.screenshots.length ? 1 : 0 }}>
        {formData.screenshots.map((shot, index) => <Box key={shot.id} sx={{ position: 'relative', width: 84, height: 72 }}>
          <Box component="img" src={shot.preview} alt={'Screenshot ' + (index + 1)} sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }} />
          <IconButton size="small" disabled={isSubmitting} onClick={() => removeScreenshot(shot.id)} aria-label={'Remove screenshot ' + (index + 1)}
            sx={{ position: 'absolute', right: 0, top: 0, bgcolor: 'background.paper' }}><Close fontSize="small" /></IconButton>
        </Box>)}
      </Box>
      {remaining > 0 && <Box {...getRootProps({ role: 'button', 'aria-label': 'Add screenshots', 'aria-disabled': isSubmitting })}
        sx={{ border: '1px dashed', borderColor: isDragActive ? 'primary.main' : 'divider', borderRadius: 2, p: 1.5, textAlign: 'center', cursor: isSubmitting ? 'default' : 'pointer' }}>
        <input {...getInputProps({ 'aria-label': 'Choose screenshots' })} />
        <CloudUploadOutlined color="action" fontSize="small" />
        <Typography variant="body2">Drop, paste or choose images</Typography>
      </Box>}
      <Typography variant="caption" color="text.secondary">{formData.screenshots.length} of 3 images ? Up to 5 MB each</Typography>
      {uploadError && <Alert severity="error" sx={{ mt: 1 }}>{uploadError}</Alert>}
    </Box>

    <FormControlLabel control={<Checkbox size="small" checked={formData.urgency} disabled={isSubmitting}
      onChange={event => setField('urgency', event.target.checked)} />} label={<Typography variant="body2">This is blocking my study work</Typography>} />

    <Box>
      <Button size="small" disabled={isSubmitting} onClick={() => setShowMore(!showMore)} aria-expanded={showMore}
        aria-controls="support-extra-details" startIcon={showMore ? <ExpandLess /> : <ExpandMore />}>More details (optional)</Button>
      <Collapse in={showMore}>
        <Box id="support-extra-details" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {has('participants') && <TextField id="support-count" label="Number of participants affected" type="number" size="small"
            disabled={isSubmitting} value={formData.participantsAffected} error={invalidCount}
            helperText={invalidCount ? 'Enter a whole number of 0 or more.' : undefined}
            onChange={event => setField('participantsAffected', event.target.value)} inputProps={{ min: 0, step: 1 }} />}
          <TextField id="support-error" label="Error message" placeholder="Paste the message you see" size="small"
            disabled={isSubmitting} value={formData.errorMessage} onChange={event => setField('errorMessage', event.target.value)} inputProps={{ maxLength: 2000 }} />
          <TextField id="support-browser" label="Browser or device" placeholder={browserInfo} size="small"
            disabled={isSubmitting} value={formData.browserDevice} onChange={event => setField('browserDevice', event.target.value)}
            helperText={'Detected: ' + browserInfo + '. Fill this in if the problem happened on another device.'} inputProps={{ maxLength: 200 }} />
          <TextField id="support-notes" label="Anything else?" multiline minRows={2} size="small" disabled={isSubmitting}
            value={formData.additionalNotes} onChange={event => setField('additionalNotes', event.target.value)} inputProps={{ maxLength: 5000 }} />
        </Box>
      </Collapse>
    </Box>
    {error && <Alert severity="error">{error}</Alert>}
    <Button variant="contained" fullWidth type="submit" disabled={formData.description.trim().length < 10 || invalidCount || isSubmitting}
      startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined} sx={{ py: 1.25, borderRadius: 2 }}>
      {isSubmitting ? 'Creating ticket?' : 'Submit ticket'}
    </Button>
  </Box>;
}
